import { type ReactElement, useEffect, useState } from 'react'

import CalendlyPopupModal from '@/components/CalendlyPopupModal'
import {
  FETCH_USER_CALL_EVENTS_KEY,
  fetchUserCallEvents,
} from '@/actions/userCallEventsActions'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { CALENDLY_RESCHEDULE_URL_BASE } from '@/features/onboarding/config'
import { Loader } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { BookedView } from './BookedView'
import { BookingView } from './BookingView'
import { LIVE_ONBOARDING_BOOKING_URL } from './constants'
import {
  eventUuidFromUri,
  formatSessionTime,
  selectLiveOnboardingCallEvent,
} from './liveOnboarding.helpers'
import { useAwaitedBooking } from './useAwaitedBooking'

type ModalMode = 'book' | 'reschedule'

export const LiveOnboardingBooking = ({
  onSkip,
}: {
  onSkip: () => void
}): ReactElement => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentUser = useReselector(getCurrentUser)
  const callEvent = useReselector(selectLiveOnboardingCallEvent)
  const callEventsLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_CALL_EVENTS_KEY
  )
  const { isAwaiting, displayedEvent, awaitBooking } =
    useAwaitedBooking(callEvent)
  const [modalMode, setModalMode] = useState<ModalMode | null>(null)
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  useEffect(() => {
    dispatch(fetchUserCallEvents())
  }, [dispatch])

  useEffect(() => {
    if (!callEventsLoading) setInitialLoadDone(true)
  }, [callEventsLoading])

  useEffect(() => {
    if (initialLoadDone) pageView('onboarding live booking')
  }, [initialLoadDone, pageView])

  if (!currentUser || (!initialLoadDone && callEventsLoading)) {
    return <Loader active />
  }

  const view = isAwaiting || callEvent ? 'booked' : 'booking'
  const currentEventUuid = callEvent?.calendlyEventId ?? null
  const rescheduleLink = displayedEvent?.rescheduleUuid
    ? `${CALENDLY_RESCHEDULE_URL_BASE}${displayedEvent.rescheduleUuid}`
    : null
  const modalUrl =
    modalMode === 'reschedule' ? rescheduleLink : LIVE_ONBOARDING_BOOKING_URL

  const handleBook = () => {
    track('live booking book clicked', { view })
    setModalMode('book')
  }
  const handleReschedule = () => {
    track('live booking reschedule clicked', { view })
    setModalMode('reschedule')
  }
  const handleSkip = () => {
    track('live booking skip clicked', { view })
    onSkip()
  }
  const handleScheduled = (eventUri?: string) => {
    awaitBooking({
      eventUuid: eventUri ? eventUuidFromUri(eventUri) : null,
      previousEventUuid: currentEventUuid,
    })
    setModalMode(null)
  }
  const handleClose = () => {
    if (modalMode === 'reschedule') {
      awaitBooking({ eventUuid: null, previousEventUuid: currentEventUuid })
    }
    setModalMode(null)
  }

  return (
    <>
      {modalMode && modalUrl && (
        <CalendlyPopupModal
          url={modalUrl}
          open
          onClose={handleClose}
          onScheduled={handleScheduled}
        />
      )}
      {view === 'booked' ? (
        <BookedView
          firstName={currentUser.firstName}
          email={currentUser.email}
          sessionTime={
            displayedEvent?.startTime
              ? formatSessionTime(displayedEvent.startTime)
              : null
          }
          onReschedule={rescheduleLink ? handleReschedule : null}
          onSkip={handleSkip}
        />
      ) : (
        <BookingView
          firstName={currentUser.firstName}
          onBook={handleBook}
          onSkip={handleSkip}
        />
      )}
    </>
  )
}
